import React, { ChangeEvent, MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';

import {
  Member,
  Programme,
  Team,
  RegistrationRequirement,
  UnassignedMember,
  Failure
} from '../../../../types/entity.types';
import { Field, RadioOrSelectOption } from '../../../../types/common.types';

import { getClassNames, isProgrammeIncomplete, mapStringOptionsToObjectOptions } from '../../../../utils/util';
import {
  ACTIVE,
  ARCHIVED,
  NEW,
  REQUIRED_REGISTRATION_FIELDS,
  UPCOMING,
  MEMBERS,
  TEAMS,
  ERROR
} from '../../../../utils/constants';
import { checkForGeneralChanges } from '../../../../utils/diff-util';
import useToaster, { ToasterActions } from '../../../../hooks/use-toaster/useToaster';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import TextInput from '../../../common/input/text-input/TextInput';
import TextArea from '../../../common/input/text-area/TextArea';
import DatePicker from '../../../common/input/date-picker/DatePicker';
import Dropdown from '../../../common/input/dropdown/Dropdown';
import Button from '../../../common/button/Button';
import Modal from '../../../common/modal/Modal';
import Checkbox from '../../../common/input/checkbox/Checkbox';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';
import MemberTable from './member-table/MemberTable';
import TeamTable from './team-table/TeamTable';

import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';
import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';
import { ReactComponent as BackIcon } from '../../../../resources/icons/back.svg';

import './general.scss';

const THEMES: string[] = ['The Healthy Whole', 'PPS'];

type Props = {
  programme?: Programme;
  members: Member[];
  unassignedMembers: UnassignedMember[];
  team?: Team;
  teams: Team[];
  createProgramme: (programmeToSave: Programme, redirectUrl?: string) => void;
  updateProgramme: (programme: Programme, redirectUrl?: string) => void;
  createMember: (memberToSave: Member, onSuccess: () => void) => void;
  updateMember: (memberToSave: Member, close?: () => void) => void;
  deleteMember: (externalReference: string, onSuccess: () => void) => void;
  downloadMemberExcel: () => void;
  uploadMemberExcel: (file: File) => void;
  activateProgramme: () => void;
  archiveProgramme: (externalReference: string) => void;
  createTeam: (team: Team, onSuccess: () => void) => void;
  updateTeam: (team: Team, onSuccess: () => void) => void;
  deleteTeam: (onSuccess: () => void) => void;
  retrieveUnassignedMembers: () => void;
  addMemberToTeam: (memberExternalReference: string, close: () => void) => void;
  removeMemberFromTeam: (memberExternalReference: string, onSuccess: () => void) => void;
  uploadTeamExcel: (file: File, onError: (value: Failure[]) => void) => void;
  downloadTeamExcel: () => void;
  sendMemberActivationCommunications: (externalReference: string, sendSMS: boolean, sendEmail: boolean) => void;
};

const General = ({
  programme,
  members,
  unassignedMembers,
  team,
  teams,
  createProgramme,
  updateProgramme,
  createMember,
  updateMember,
  deleteMember,
  downloadMemberExcel,
  uploadMemberExcel,
  activateProgramme,
  archiveProgramme,
  createTeam,
  updateTeam,
  deleteTeam,
  retrieveUnassignedMembers,
  addMemberToTeam,
  removeMemberFromTeam,
  uploadTeamExcel,
  downloadTeamExcel,
  sendMemberActivationCommunications
}: Props): ReactElement => {
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [hasRegistrationRequirements, setHasRegistrationRequirements] = useState<boolean>(false);

  const [programmeName, setProgrammeName] = useState<string | undefined>('');
  const [programmeDescription, setProgrammeDescription] = useState<string | undefined>('');
  const [programmeFromDate, setProgrammeFromDate] = useState<Date | null>(null);
  const [programmeToDate, setProgrammeToDate] = useState<Date | null>(null);
  const [programmeTheme, setProgrammeTheme] = useState<string | undefined>(undefined);
  const [programmeIsNutrition, setProgrammeIsNutrition] = useState<boolean>(true);
  const [programmeIsMovement, setProgrammeIsMovement] = useState<boolean>(true);
  const [programmeIsSleep, setProgrammeIsSleep] = useState<boolean>(true);
  const [programmeIsCondition, setProgrammeIsCondition] = useState<boolean>(true);
  const [programmeIsCharity, setProgrammeIsCharity] = useState<boolean>(true);
  const [programmePrice, setProgrammePrice] = useState<string>('');
  const [programmeFields, setProgrammeFields] = useState<Field[] | string[]>([]);
  const [programmeLeaderboardCollectFrom, setProgrammeLeaderboardCollectFrom] = useState<Date | null>(null);

  const [payfastMerchantId, setPayfastMerchantId] = useState<string>('');
  const [payfastMerchantKey, setPayfastMerchantKey] = useState<string>('');

  const scrollRef: MutableRefObject<HTMLSpanElement | null> = useRef<HTMLInputElement | null>(null);

  const navigate: NavigateFunction = useNavigate();

  const { showToaster }: ToasterActions = useToaster();

  const { view, programmeExternalReference, status }: Params = useParams();

  useEffect((): void => {
    if (programme?.externalReference) {
      setProgrammeName(programme.name);
      setProgrammeDescription(programme.description);

      if (programme.fromDate) {
        setProgrammeFromDate(new Date(programme.fromDate));
      }
      if (programme.toDate) {
        setProgrammeToDate(new Date(programme.toDate));
      }

      setProgrammeTheme(programme.theme);
      setProgrammeIsNutrition(programme.isNutrition ?? false);
      setProgrammeIsMovement(programme.isMovement ?? false);
      setProgrammeIsSleep(programme.isSleep ?? false);
      setProgrammeIsCondition(programme.isCondition ?? false);
      setProgrammeIsCharity(programme.charityWork ?? false);

      if (programme.leaderboardCollectFrom) {
        setProgrammeLeaderboardCollectFrom(new Date(programme.leaderboardCollectFrom));
      }

      setHasRegistrationRequirements(programme.hasRegistrationRequirements ?? false);
      setProgrammeFields(programme.registrationRequirement?.requiredRegistrationFields || []);

      const { registrationRequirement } = programme;
      if (registrationRequirement) {
        setPayfastMerchantId(registrationRequirement.payfastMerchantId ?? '');
        setPayfastMerchantKey(registrationRequirement.payfastMerchantKey ?? '');
        setProgrammePrice(registrationRequirement.programmePrice);

        if (registrationRequirement.requiredRegistrationFields?.length === undefined) {
          return;
        }

        const length: number = registrationRequirement.requiredRegistrationFields.length;

        if (length > 0) {
          setProgrammeFields([]);
          const mappedFields: Field[] = (registrationRequirement.requiredRegistrationFields as string[]).reduce(
            (acc: Field[], field: string): Field[] => {
              const attributes: string[] = field.split('**');

              if (!attributes[0] || !attributes[1] || !attributes[2]) {
                return acc;
              }

              const fields: Field = {
                label: attributes[0],
                optional: attributes[1] === 'optional',
                type: attributes[2]
              };

              acc.push(fields);

              return acc;
            },
            []
          );

          setProgrammeFields(mappedFields);
        }
      }
    }

    setIsSettingsOpen(false);
    setRedirectUrl(undefined);
  }, [status, programme, members]);

  useEffect((): void => {
    if (status === NEW) {
      setProgrammeName('');
      setProgrammeDescription('');
      setProgrammeFromDate(null);
      setProgrammeToDate(null);
      setProgrammeTheme(undefined);
      setProgrammeIsNutrition(true);
      setProgrammeIsMovement(true);
      setProgrammeIsSleep(true);
      setProgrammeIsCondition(true);
      setProgrammeIsCharity(true);
      setProgrammeLeaderboardCollectFrom(null);
      setHasRegistrationRequirements(false);
      setPayfastMerchantId('');
      setPayfastMerchantKey('');
      setProgrammePrice('');
      setProgrammeFields([]);
    }
  }, [status]);

  const handleSaveClick = (): void => {
    if (!programmeFromDate) {
      showToaster(ERROR, 'Please provide a from Date.');
      return;
    }

    if (!programmeToDate) {
      showToaster(ERROR, 'Please provide a to Date.');
      return;
    }

    if (!programmeTheme) {
      showToaster(ERROR, 'Please provide a Programme Theme.');
      return;
    }

    const programmeToSave: Programme = {
      name: programmeName,
      description: programmeDescription,
      fromDate: programmeFromDate,
      toDate: programmeToDate,
      theme: programmeTheme,
      isNutrition: programmeIsNutrition,
      isMovement: programmeIsMovement,
      isSleep: programmeIsSleep,
      isCondition: programmeIsCondition,
      charityWork: programmeIsCharity,
      status: status === NEW ? UPCOMING : status,
      hasRegistrationRequirements,
      leaderboardCollectFrom: programmeLeaderboardCollectFrom ?? null,
      registrationRequirement: hasRegistrationRequirements
        ? {
            requiredRegistrationFields: (programmeFields as Field[])?.map(
              (field: Field): string => `${field.label}**${field.optional ? 'optional' : 'required'}**${field.type}`
            ),
            payfastMerchantId,
            payfastMerchantKey,
            programmePrice
          }
        : null
    };

    if (programme?.externalReference) {
      updateProgramme(
        {
          ...programmeToSave,
          externalReference: programme.externalReference
        },
        redirectUrl
      );
    } else {
      createProgramme(programmeToSave, redirectUrl);
    }

    setRedirectUrl(undefined);
  };

  const handleArchiveConfirmationClick = (): void => {
    if (!programme?.externalReference) {
      return;
    }
    archiveProgramme(programme.externalReference);
    setShowArchiveModal(false);
  };

  const handleAddRequiredFieldClick = (): void => {
    if (!programmeFields) {
      return;
    }

    const fields: Field[] = [
      {
        label: '',
        optional: false,
        type: 'text'
      }
    ];

    setProgrammeFields([...(programmeFields as Field[]), ...fields]);
    if (status !== UPCOMING) {
      setTimeout((): void => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const handleRequiredFieldLabelChange = (value: string, index: number): void => {
    const fields: Field[] = [...programmeFields] as Field[];
    if (!fields[index]) {
      return;
    }
    fields[index].label = value;
    setProgrammeFields(fields);
  };

  const handleRequiredFieldOptionalChange = (value: boolean, index: number): void => {
    const fields: Field[] = [...programmeFields] as Field[];
    if (!fields[index]) {
      return;
    }
    fields[index].optional = value;
    setProgrammeFields(fields);
  };

  const handleRequiredFieldCheckboxChange = (value: boolean, index: number): void => {
    const fields: Field[] = [...programmeFields] as Field[];
    if (!fields[index]) {
      return;
    }
    fields[index].type = value ? 'checkbox' : 'text';
    setProgrammeFields(fields);
  };

  const handleDeleteRequiredFieldClick = (index: number): void => {
    const fields: Field[] = [...programmeFields] as Field[];
    fields.splice(index, 1);
    setProgrammeFields(fields);
  };

  const checkForUnsavedChanges = (url: string): void => {
    let registrationRequirement: RegistrationRequirement | null = null;

    if (hasRegistrationRequirements) {
      const requiredRegistrationFields: string[] = (programmeFields as Field[])?.map(
        (field: Field): string => `${field.label}**${field.optional ? 'optional' : 'required'}**${field.type}`
      );

      registrationRequirement = {
        requiredRegistrationFields,
        payfastMerchantId,
        payfastMerchantKey,
        programmePrice
      };
    }

    if (!programmeFromDate) {
      showToaster(ERROR, 'Please provide a from Date.');
      return;
    }

    if (!programmeToDate) {
      showToaster(ERROR, 'Please provide a to Date.');
      return;
    }

    if (!programmeTheme) {
      showToaster(ERROR, 'Please provide a Programme Theme.');
      return;
    }

    const programmeToSave: Programme = {
      ...programme,
      name: programmeName,
      description: programmeDescription,
      fromDate: programmeFromDate,
      toDate: programmeToDate,
      theme: programmeTheme,
      isNutrition: programmeIsNutrition,
      isMovement: programmeIsMovement,
      isSleep: programmeIsSleep,
      isCondition: programmeIsCondition,
      leaderboardCollectFrom: programmeLeaderboardCollectFrom,
      hasRegistrationRequirements,
      registrationRequirement
    };

    if (checkForGeneralChanges(programme, programmeToSave)) {
      setRedirectUrl(url);
    } else {
      navigate(url);
    }
  };

  const handleToggleClick = (newView: string): void => {
    if (newView === view) return;

    navigate(`/programmes/${status}/${programmeExternalReference}/general/${newView}`);
  };

  const renderToggle = (upcoming: boolean): ReactElement => {
    return (
      <div className={getClassNames('general__toggle', { upcoming })}>
        <button
          className={getClassNames('general__toggle-button', { active: view === MEMBERS })}
          onClick={(): void => handleToggleClick(MEMBERS)}
        >
          Members
        </button>
        <button
          className={getClassNames('general__toggle-button', { active: view === TEAMS })}
          onClick={(): void => handleToggleClick(TEAMS)}
        >
          Teams
        </button>

        {team && (
          <button className="general__toggle-back-button" onClick={(): void => navigate(-1)}>
            <BackIcon className="general__toggle-back-button__icon" />
            <p>Back</p>
          </button>
        )}
      </div>
    );
  };

  const renderTable = (): ReactElement => {
    if (view === TEAMS) {
      if (teams) {
        return (
          <TeamTable
            team={team}
            teams={teams}
            members={unassignedMembers}
            createTeam={createTeam}
            updateTeam={updateTeam}
            deleteTeam={deleteTeam}
            retrieveUnassignedMembers={retrieveUnassignedMembers}
            addMemberToTeam={addMemberToTeam}
            removeMemberFromTeam={removeMemberFromTeam}
            uploadTeamExcel={uploadTeamExcel}
            downloadTeamExcel={downloadTeamExcel}
          />
        );
      }
    }

    return (
      <MemberTable
        members={members}
        createMember={createMember}
        updateMember={updateMember}
        deleteMember={deleteMember}
        uploadMemberExcel={uploadMemberExcel}
        downloadMemberExcel={downloadMemberExcel}
        sendMemberActivationCommunications={sendMemberActivationCommunications}
      />
    );
  };

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="general" data-testid={`general-${status}`}>
        <div className="general__icons">
          {((isSettingsOpen && status !== ARCHIVED) || status === UPCOMING || status === NEW) && (
            <div className="general__icon-container" onClick={handleSaveClick}>
              <SaveIcon className="general__save-icon" data-testid="save-icon" />
            </div>
          )}
          {!isSettingsOpen && status !== UPCOMING && status !== NEW && (
            <div className="general__icon-container" onClick={(): void => setIsSettingsOpen(true)}>
              <GearIcon className="general__gear-icon" data-testid="cog-icon" />
            </div>
          )}
          {isSettingsOpen && status !== UPCOMING && status !== NEW && (
            <div className="general__icon-container" onClick={(): void => setIsSettingsOpen(false)}>
              <CloseIcon className="general__close-icon" data-testid="close-icon" />
            </div>
          )}
        </div>

        <div className="general__scroll-container">
          {status === ACTIVE && isSettingsOpen && (
            <Button
              text="Archive"
              onClick={(): void => setShowArchiveModal(true)}
              disabled={isProgrammeIncomplete(programme)}
            />
          )}
          {status !== UPCOMING && status !== NEW && (
            <div className="general__header-container">
              {!isSettingsOpen && renderToggle(false)}
              {isSettingsOpen && <h1>Settings</h1>}
            </div>
          )}
          {status === UPCOMING && (
            <Button text="Publish" onClick={activateProgramme} disabled={isProgrammeIncomplete(programme)} />
          )}

          {(status === UPCOMING || status === NEW) && (
            <div className="general__header-container">
              <h1>Settings</h1>
            </div>
          )}
          {(status === UPCOMING || status === NEW || isSettingsOpen) && (
            <div className="general__input-container">
              <TextInput
                label="Programme Name:"
                name="programme"
                value={programmeName ?? ''}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setProgrammeName(target.value)}
                disabled={status === ARCHIVED}
              />
              <TextArea
                label="Description:"
                name="description"
                value={programmeDescription ?? ''}
                onChange={({ target }: ChangeEvent<HTMLTextAreaElement>): void => setProgrammeDescription(target.value)}
                disabled={status === ARCHIVED}
              />
              <div className="general__input-container__datepicker-container">
                <DatePicker
                  label="Running from:"
                  name="running-from"
                  value={programmeFromDate as Date}
                  onChange={(date: Date): void => {
                    if (!programmeLeaderboardCollectFrom || programmeLeaderboardCollectFrom === programmeFromDate) {
                      setProgrammeLeaderboardCollectFrom(date);
                    }

                    setProgrammeFromDate(date);
                  }}
                  disabled={status === ARCHIVED}
                />
                <DatePicker
                  label="To:"
                  name="running-to"
                  value={programmeToDate as Date}
                  onChange={(date: Date): void => setProgrammeToDate(date)}
                  disabled={status === ARCHIVED}
                />
              </div>
              <Dropdown
                label="Theme:"
                name="theme"
                options={mapStringOptionsToObjectOptions(THEMES)}
                value={{ label: programmeTheme ?? '', value: programmeTheme ?? '' }}
                onChange={(option: RadioOrSelectOption): void => setProgrammeTheme(option.value)}
                disabled={status === ARCHIVED}
              />

              <div className="general__header-container">
                <h1>Active Features</h1>
              </div>

              <div className="general__input-container__checkbox-container">
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Nutrition:"
                    name="is-nutrition"
                    value={programmeIsNutrition ?? false}
                    onChange={(value: boolean): void => setProgrammeIsNutrition(value)}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Movement:"
                    name="is-movement"
                    value={programmeIsMovement ?? false}
                    onChange={(value: boolean): void => setProgrammeIsMovement(value)}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Self-care Sleep:"
                    name="is-sleep"
                    value={programmeIsSleep ?? false}
                    onChange={(value: boolean): void => setProgrammeIsSleep(value)}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Condition:"
                    name="is-condition"
                    value={programmeIsCondition ?? false}
                    onChange={(value: boolean): void => setProgrammeIsCondition(value)}
                  />
                </div>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    label="Charity Work:"
                    name="is-charity"
                    value={programmeIsCharity ?? false}
                    onChange={(value: boolean): void => setProgrammeIsCharity(value)}
                  />
                </div>
              </div>

              <div className="general__header-container">
                <h1>Leaderboard Settings</h1>
              </div>

              <div className="general__input-container__leaderboard-settings">
                <div className="general__input-container__datepicker-container">
                  <DatePicker
                    label="Collect data from:"
                    name="collect-data-from"
                    value={
                      (programmeLeaderboardCollectFrom ? programmeLeaderboardCollectFrom : programmeFromDate) as Date
                    }
                    onChange={(date: Date): void => setProgrammeLeaderboardCollectFrom(date)}
                    disabled={status === ARCHIVED}
                  />
                </div>
              </div>

              <div className="general__header-container">
                <h1>Registration Requirements</h1>
                <div className="general__input-container__checkbox">
                  <Checkbox
                    name="registration-requirements"
                    value={hasRegistrationRequirements ?? false}
                    onChange={setHasRegistrationRequirements}
                  />
                </div>
              </div>

              {hasRegistrationRequirements && (
                <div className="general__registration-requirements">
                  <TextInput
                    label="PayFast Merchant ID:"
                    name="pay-fast-merchant-id"
                    value={payfastMerchantId}
                    onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setPayfastMerchantId(target.value)}
                    disabled={status === ARCHIVED}
                  />
                  <TextInput
                    label="PayFast Merchant Key:"
                    name="pay-fast-merchant-key"
                    value={payfastMerchantKey}
                    onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setPayfastMerchantKey(target.value)}
                    disabled={status === ARCHIVED}
                  />
                  <TextInput
                    label="Programme Price:"
                    name="programme-price"
                    value={programmePrice}
                    onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setProgrammePrice(target.value)}
                    disabled={status === ARCHIVED}
                  />

                  <div className="general__registration-requirements__required-fields">
                    <p className="general__registration-requirements__required-fields__label">Required Fields:</p>
                    <div className="general__registration-requirements__required-fields__fields">
                      {REQUIRED_REGISTRATION_FIELDS.map(
                        (field: Field): ReactElement => (
                          <p
                            className="general__registration-requirements__required-fields__default-field"
                            key={field.label}
                          >
                            {field.label}
                          </p>
                        )
                      )}
                      {(programmeFields as Field[])?.map(
                        (field: Field, index: number): ReactElement => (
                          <div className="general__registration-requirements__required-fields__field" key={index}>
                            <TextInput
                              name={`field-${index}`}
                              value={field.label}
                              onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                                handleRequiredFieldLabelChange(target.value, index)
                              }
                              disabled={status === ARCHIVED}
                            />

                            <div className="general__registration-requirements__required-fields__field__checkbox-container">
                              <Checkbox
                                label="Optional?"
                                value={field.optional}
                                onChange={(value: boolean): void => handleRequiredFieldOptionalChange(value, index)}
                              />
                            </div>

                            <div className="general__registration-requirements__required-fields__field__checkbox-container">
                              <Checkbox
                                label="Checkbox?"
                                value={field.type === 'checkbox'}
                                onChange={(value: boolean): void => handleRequiredFieldCheckboxChange(value, index)}
                              />
                            </div>

                            <CloseIcon
                              className="general__registration-requirements__required-fields__field__delete-icon"
                              onClick={(): void => handleDeleteRequiredFieldClick(index)}
                              data-testid="delete-required-field-button"
                            />
                          </div>
                        )
                      )}
                      <Button text="Add" onClick={handleAddRequiredFieldClick} />
                      <span ref={scrollRef} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === UPCOMING && (
            <>
              {renderToggle(true)}
              {renderTable()}
            </>
          )}

          {status !== UPCOMING && status !== NEW && !isSettingsOpen && renderTable()}
        </div>
      </div>

      <Modal show={showArchiveModal}>
        <div className="general__archive-modal" data-testid="member-modal">
          <div className="general__archive-modal__header">Archive Programme</div>
          <p className="general__archive-modal__message">Are you sure you want to archive this programme?</p>
          <div className="general__archive-modal__button-container">
            <Button text="Cancel" onClick={(): void => setShowArchiveModal(false)} />
            <Button text="Archive" onClick={handleArchiveConfirmationClick} />
          </div>
        </div>
      </Modal>

      <SaveChangesModal redirectUrl={redirectUrl} handleSaveClick={handleSaveClick} />
    </ProgrammeLayout>
  );
};

export default General;
